import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { Component, createRef } from 'react';
import PT from 'prop-types';
import styled, { css } from 'styled-components';
import { i as isShadowRootInstance } from '../prop-types/isShadowRoot-C-aOK5mv.js';
import { L as LabelLine } from '../FormGroup/LabelLine-CbVig6Vl.js';
import { W as Wrapper } from './Dropdown.styles-Ds46Mep3.js';
import { D as DropdownContainer } from './DropdownContainer-C0fPa2Sr.js';
import { i as isClickableListItem } from '../SelectList/utils/isClickableListItem-3xl9P76P.js';
import { d as defaultValueSerializer } from '../SelectList/utils/defaultValueSerializer-BrPGuJ5m.js';
import { D as DropdownButton } from './DropdownButton-qGPueNyR.js';
import { D as DropdownIsMobileProvider } from './DropdownIsMobileContext-DIYFOYAu.js';
import { D as DropdownItem } from './DropdownItem/DropdownItem-BwO7oFG3.js';
import { D as DropdownItemGroup } from './DropdownItemGroup/DropdownItemGroup-XzM3grRS.js';
import { T as TaktIdConsumer, w as withTaktFallbackId } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';
import { i as isString } from '../LocalModules/storm-ui-utils/dist/esm/isString-D4CFRAVS-D4CFRAVS.js';
import { c as composeRefs } from '../LocalModules/storm-ui-utils/dist/esm/composeRefs-CHPW6wJ8-CHPW6wJ8.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const StyledLabelLine = /*#__PURE__*/styled(LabelLine).withConfig({
  componentId: "sc-storm-ui-30065889__sc-patzmg-0"
})(["label&&{align-self:center;margin-bottom:2px;", "}"], ({
  $inline,
  theme
}) => $inline ? css(["display:inline-block;margin-bottom:0;margin-inline-end:", ";margin-inline-start:0;"], theme.spacing.mini) : css(["display:block;"]));
const DropdownLabelLine = withTaktFallbackId(StyledLabelLine);
class Dropdown extends Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "toggleRef", /*#__PURE__*/createRef());
    _defineProperty(this, "labelRef", /*#__PURE__*/createRef());
    _defineProperty(this, "handleChange", (value, event) => {
      const {
        onChange
      } = this.props;
      onChange === null || onChange === void 0 || onChange(value, event);
    });
    _defineProperty(this, "togglePopoverView", () => {
      const {
        disabled,
        forceOpen
      } = this.props;
      if (!disabled && !forceOpen) {
        this.setState(prevState => ({
          isPopoverVisible: !prevState.isPopoverVisible
        }));
      }
    });
    _defineProperty(this, "togglePopoverViewFromPopper", event => {
      var _this$labelRef$curren;
      if (!event) {
        this.togglePopoverView();
      } else if (event && event.target instanceof Node && this.labelRef && !((_this$labelRef$curren = this.labelRef.current) !== null && _this$labelRef$curren !== void 0 && _this$labelRef$curren.contains(event.target))) {
        this.togglePopoverView();
      }
    });
    _defineProperty(this, "getCurrentLabel", () => {
      const {
        selectedValue,
        children
      } = this.props;
      let currentLabel = '';
      React__default.Children.forEach(children, item => {
        /*
         * We use the `name` static member to determine if the child is a DropdownItem.
         * We can not directly check if it is the same instance of the component
         * because the hot loader will create new instances in dev mode. Then the comparison
         * will fail incorrectly.
         */
        if (isClickableListItem(item)) {
          if ((item === null || item === void 0 ? void 0 : item.type.displayName) === DropdownItem.displayName) {
            if (JSON.stringify(selectedValue) === JSON.stringify(item.props.value)) {
              currentLabel = item.props.children;
            }
          }
          if ((item === null || item === void 0 ? void 0 : item.type.displayName) === DropdownItemGroup.displayName) {
            React__default.Children.forEach(item.props.children, option => {
              if (JSON.stringify(selectedValue) === JSON.stringify(option.props.value)) {
                currentLabel = option.props.children;
              }
            });
          }
        }
      });
      return currentLabel;
    });
    _defineProperty(this, "getButtonText", () => {
      const {
        selectedLabelOverride,
        onOverrideLabel,
        selectedValue
      } = this.props;
      const currentLabel = this.getCurrentLabel();
      return selectedLabelOverride || (onOverrideLabel === null || onOverrideLabel === void 0 ? void 0 : onOverrideLabel(currentLabel, selectedValue));
    });
    this.state = {
      isPopoverVisible: !!props.forceOpen
    };
  }
  componentDidUpdate({
    forceOpen: prevForceOpen
  }, {
    isPopoverVisible: prevIsPopoverVisible
  }) {
    const {
      onOpen,
      onClose,
      forceOpen
    } = this.props;
    const {
      isPopoverVisible
    } = this.state;
    if (isPopoverVisible !== prevIsPopoverVisible) {
      if (isPopoverVisible) {
        onOpen === null || onOpen === void 0 || onOpen();
      } else {
        onClose === null || onClose === void 0 || onClose();
      }
    }
    if (!prevForceOpen && forceOpen) {
      this.setState({
        isPopoverVisible: true
      });
    }
  }
  render() {
    const {
      children,
      selectedValue,
      selectedLabelOverride,
      onOverrideLabel,
      forceOpen,
      onChange,
      small,
      preRender,
      postRender,
      onOpen,
      onClose,
      inline,
      hideLabel,
      label,
      fullWidth,
      disabled,
      noWrapDropdownItems,
      focusSelected,
      error,
      shadowRoot,
      buttonRef,
      id,
      renderLabelStart,
      renderLabelEnd,
      transparentButton,
      secondaryViewCloseButtonLabel,
      mobileFullScreen,
      taktId,
      taktValue,
      popperRef,
      openDirection,
      forceDirection,
      ...rest
    } = this.props;
    const {
      name
    } = this.props;
    const {
      isPopoverVisible
    } = this.state;
    const buttonText = this.getButtonText();
    return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
      taktId: taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('dropdown')
    }, ({
      getDataTaktAttributes
    }) => /*#__PURE__*/React__default.createElement(DropdownIsMobileProvider, {
      mobileFullScreen: mobileFullScreen
    }, /*#__PURE__*/React__default.createElement(Wrapper, {
      $fullWidth: fullWidth
    }, label && /*#__PURE__*/React__default.createElement(DropdownLabelLine, _extends({}, getDataTaktAttributes({
      taktIdSuffix: 'label'
    }), {
      innerRef: this.labelRef,
      $inline: inline,
      hidden: hideLabel,
      renderLabelStart: renderLabelStart,
      renderLabelEnd: renderLabelEnd,
      onLabelClick: this.togglePopoverView
    }, isString(id) ? {
      labelFor: id
    } : undefined), label), /*#__PURE__*/React__default.createElement(DropdownContainer, {
      secondaryViewCloseButtonLabel: secondaryViewCloseButtonLabel,
      anchorEl: this.toggleRef,
      isOpen: isPopoverVisible,
      onClose: this.togglePopoverViewFromPopper,
      shadowRoot: shadowRoot,
      trigger: triggerProps => /*#__PURE__*/React__default.createElement(DropdownButton, _extends({}, rest, getDataTaktAttributes({
        taktIdSuffix: 'trigger-button'
      }), {
        $inline: inline,
        disabled: disabled,
        small: small,
        onClick: this.togglePopoverView,
        buttonRef: composeRefs(buttonRef, this.toggleRef, triggerProps === null || triggerProps === void 0 ? void 0 : triggerProps.setTriggerElement),
        "aria-haspopup": "listbox",
        "aria-label": `${label}${buttonText ? ` ${buttonText}` : ''}`,
        "aria-invalid": error,
        $error: error,
        transparent: transparentButton
      }, isString(id) ? {
        id
      } : undefined, {
        buttonText: buttonText
      })),
      preRender: preRender,
      postRender: postRender,
      selectedValue: selectedValue,
      onChange: this.handleChange,
      noWrap: noWrapDropdownItems,
      name: "",
      focusSelected: focusSelected,
      forceOpen: forceOpen,
      popperRef: popperRef,
      openDirection: openDirection,
      forceDirection: forceDirection
    }, children), /*#__PURE__*/React__default.createElement("input", {
      type: "hidden",
      name: name,
      value: defaultValueSerializer(selectedValue)
    }))));
  }
}
_defineProperty(Dropdown, "propTypes", {
  /*
   * We DO want `any` allowed to be passed in because we can not assume the type that a consumer
   * will need to use.
   */
  // eslint-disable-next-line
  selectedValue: PT.any,
  /**
   * `<DropdownItem />` usually, but can also be `<DropdownDivider />` or `<DropdownItemGroup />`
   * other react elements. Only DropdownItems will be used for selection purposes.
   */
  children: PT.node.isRequired,
  /**
   * Function called when the user selects a new item. Passes the value and event.
   */
  onChange: PT.func,
  /**
   * Specifies that the dropdown should be a smaller size than default.
   */
  small: PT.bool,
  /**
   * Overrides the placeholder text for the dropdown.
   */
  selectedLabelOverride: PT.string,
  /**
   * Hook in to this to display the "selected" label differently than how
   * it appears in the list item.
   */
  onOverrideLabel: PT.func,
  /**
   * Allows you to force dropdown to be open. Leave `undefined` to keep normal behavior.
   */
  forceOpen: PT.bool,
  /**
   * Moves the label inline.
   */
  inline: PT.bool,
  /**
   * Can be used to render elements prior to the `<SelectList />`. Useful to have a sticky
   * section which does not scroll with long dropdown lists.
   */
  preRender: PT.func,
  /**
   * Can be used to render elements after the `<SelectList />`. Useful to have a sticky
   * section which does not scroll with long dropdown lists.
   */
  postRender: PT.func,
  /**
   * Function called when the dropdown is opened.
   */
  onOpen: PT.func,
  /**
   * Function called when the dropdown is closed.
   */
  onClose: PT.func,
  /**
   * The text label for the dropdown.
   */
  label: PT.string,
  /**
   * Labels are required for screen reader accessibility, but can be hidden visually.
   */
  hideLabel: PT.bool,
  /**
   * Force dropdown to fill container (instead of matching content). Can be used to
   * size custom by giving container a width.
   */
  fullWidth: PT.bool,
  /**
   * Specifies if the control is disabled, which prevents the user from modifying the value and
   * prevents the value from being included in a form submission. A disabled control can't receive
   * focus.
   */
  disabled: PT.bool,
  /**
   * Prevents dropdown items from wrapping text on whitespace. This is useful for browser such as
   * Firefox and Safari that consume some of the dropdown space to place a scrollbar.
   */
  noWrapDropdownItems: PT.bool,
  /**
   * When the dropdown opened the currently selected item takes input focus.
   */
  focusSelected: PT.bool,
  /**
   * Displays a red border and sets aria-invalid="true" when true. If a dropdown is in an invalid
   * state, pair and describe it with an `<InlineMessage error />` to explain the problem.
   */
  error: PT.bool,
  /**
   * Ref to the dropdown toggle button element.
   */
  shadowRoot: isShadowRootInstance,
  /**
   * The page unique identifier of the input.
   */
  id: PT.string,
  /**
   * Used to render content *before* the label, but outside the click-able area.
   */
  renderLabelStart: PT.oneOfType([PT.func, PT.node]),
  /**
   * Used to render content *after* the label, but outside the click-able area.
   */
  renderLabelEnd: PT.oneOfType([PT.func, PT.node]),
  /**
   * Used to render dropdown button with no background.
   */
  transparentButton: PT.bool,
  /**
   * Used to render dropdown in SecondaryView on mobile device.
   */
  mobileFullScreen: PT.bool,
  /**
   * Accepts elements to be rendered inside the close button in the SecondaryView's header.
   */
  secondaryViewCloseButtonLabel: PT.node,
  /**
   * Position the popover element relative to the trigger.
   */
  openDirection: PT.string,
  /**
   * Set to true if need to force the placement of the popover element.
   */
  forceDirection: PT.bool
});
_defineProperty(Dropdown, "defaultProps", {
  selectedValue: '',
  small: false,
  onChange: noop,
  onOpen: noop,
  onClose: noop,
  preRender: undefined,
  postRender: undefined,
  onOverrideLabel: label => label,
  forceOpen: false,
  inline: false,
  selectedLabelOverride: undefined,
  label: '',
  hideLabel: false,
  fullWidth: false,
  disabled: false,
  noWrapDropdownItems: false,
  focusSelected: true,
  error: false,
  shadowRoot: undefined,
  id: undefined,
  renderLabelStart: undefined,
  renderLabelEnd: undefined,
  transparentButton: false,
  mobileFullScreen: false,
  secondaryViewCloseButtonLabel: 'Done',
  openDirection: 'bottom',
  forceDirection: false
});

export { Dropdown as D };
//# sourceMappingURL=Dropdown-BVDSKlZi.js.map
